import {
  Button,
  Group,
  Loader,
  Stack,
  ThemeIcon,
  Text,
  Badge,
} from "@mantine/core";
import { Event } from "../../common/events/event.schema";
import {
  IconCheck,
  IconClock,
  IconBrandStripe,
  IconCoin,
  IconTrash,
  IconCoins,
} from "@tabler/icons-react";
import { getBoardCurrencySymbol } from "../../common/util";
import { modals, openContextModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { trackEvent } from "../../common/analytics";
import {
  createEventRegistration,
  deleteEventRegistration,
  eventCheckin,
  createTournamentPaymentLink,
  payTournamentWithToken,
} from "../../common/api";
import { Member } from "../../common/members/member.schema";
import { useAuthStore } from "../../common/stores/auth.store";
import { TokenType } from "../../common/payments/tokentype.schema";
import { useIsBoardReferee } from "../../common/boards/board.hooks";
import { Board } from "../../common/boards/board.schema";
import {
  listTokenBalances,
  listTokenPacks,
} from "../../common/payments/payments.api";

const SelfRegistration = ({ event, board }: { event: Event; board: Board }) => {
  const queryClient = useQueryClient();
  const tokenType = event.tokenType as TokenType | undefined;
  //   console.log("tokenType :", tokenType);
  const user = useAuthStore((s) => s.user);
  const { data: tokenBalanceRes } = useQuery(
    ["tokenBalances", event._id],
    () =>
      listTokenBalances({
        params: { tokenType: tokenType?._id, user: user?._id },
      }),
    { enabled: !!user }
  );
  const tokenBalance = tokenBalanceRes?.records?.[0];
  //   console.log("tokenBalance :", tokenBalance);
  const { mutate: register } = useMutation(createEventRegistration, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const { mutate: unregister } = useMutation(deleteEventRegistration, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const { mutate: checkin, isLoading: checkingIn } = useMutation(eventCheckin, {
    onSuccess: () => {
      router.replace(router.asPath.split("?")[0]);
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const token = useAuthStore((s) => s.token);
  const { mutate: openStripePayment, isLoading: isLoadingStripePayment } =
    useMutation((event: Event) => createTournamentPaymentLink(event), {
      onSuccess: (data) => {
        // console.log(data);
        // window.open(data.url, "_blank");
        window.open(data.url, "_self");
      },
    });
  const selfMember =
    user &&
    event?.members.length &&
    (event.members as Member[]).find((m) => m.user === user._id);
  const selfRegistration =
    user && event.registrations?.find((r) => r.user === user._id);
  const onClickRegister = () => {
    if (user) {
      let valid = true;
      if (event.requireUsfaMemberNumber && !user.usfaMemberNumber)
        valid = false;
      if (
        (event.requireFullName || event.requireUsfaMemberNumber) &&
        (!user.name || !user.lastName)
      )
        valid = false;
      if (valid) {
        register(event._id);
      } else {
        trackEvent("open_modal_event_registration", { board: board?._id });
        openContextModal({
          modal: "invalidRegistration",
          innerProps: { event },
          title: "Extra details required",
        });
      }
    } else {
      router.push(`/login?state=register&redirect=${router.asPath}`);
    }
  };
  const { mutate: payWithToken } = useMutation(
    async () => {
      if (!event) return;
      if (!tokenBalance) return;
      await payTournamentWithToken(event._id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tokenBalances"]);
        queryClient.invalidateQueries(["event"]);
        queryClient.refetchQueries();
      },
    }
  );
  const showTokenPackOptions = () => {
    trackEvent("open_modal_token_pack_options", { tokenType: tokenType?._id });
    openContextModal({
      title: "Purchase a token pack",
      modal: "purchaseTokenPack",
      innerProps: { tokenType, board },
    });
  };
  const { data: tokenPacksRes } = useQuery(["tokenPacks", tokenType?._id], () =>
    listTokenPacks({
      params: { tokenType: tokenType?._id },
    })
  );
  const tokenPacks = tokenPacksRes?.records || [];
  const onClickUnregister = () => {
    if (!user) return;
    if (!selfRegistration) return;
    modals.openConfirmModal({
      title: "Are you sure you want to unregister?",
      children: (
        <Stack>
          {selfRegistration.paid && (
            <Text>
              You will need to contact an administrator to refund your entry
              fee.
            </Text>
          )}
        </Stack>
      ),
      confirmProps: { color: "red" },
      onConfirm: () => {
        unregister({
          eventId: event._id,
          registrationId: selfRegistration._id,
        });
      },
      labels: {
        cancel: "Cancel",
        confirm: "Unregister",
      },
    });
  };
  const router = useRouter();
  const isRef = useIsBoardReferee();
  const atCapacity =
    !!event.maxMembers && event.registrations.length >= event.maxMembers;
  const canRegister =
    !event.started &&
    event.openRegistration &&
    !selfMember &&
    !selfRegistration;
  const userHasToken = tokenBalance && tokenBalance.quantity > 0;
  const canPayWithToken =
    event.requireRegistrationPayment && !selfRegistration?.paid && tokenType;
  const canPurchaseTokens = event.tokenType && tokenPacks.length > 0;
  useEffect(() => {
    const checkedIn = !!selfRegistration?.checkedIn;
    const registered = !!selfRegistration;

    if (!checkedIn && router.query.checkin === "true") {
      if (!token) {
        router.replace("/login?redirect=" + router.asPath);
        return;
      }
      // console.log("doing checkin");
      if (registered) {
        checkin(event._id);
      } else {
        if (event.openRegistration) {
          register(event._id);
        } else {
          showNotification({
            message:
              "Cannot check in for this event as you are not registered, please contact the event administrator",
            color: "red",
            autoClose: true,
          });
        }
      }
    }
  }, [checkin, register, router, selfRegistration, token, event]);
  return (
    <Stack>
      {canRegister && (
        <>
          {event.requireRegistrationPayment && tokenType && (
            <Group justify="right">
              <Badge variant="light">
                {tokenType.name}
                {userHasToken && canPayWithToken && (
                  <> ({tokenBalance.quantity} available)</>
                )}
              </Badge>
            </Group>
          )}
          <Button
            variant={isRef ? "default" : "filled"}
            onClick={onClickRegister}
            disabled={atCapacity}
          >
            <Group>
              <Text>Register for this event</Text>
              {event.requireRegistrationPayment && event.registrationPrice && (
                <Badge variant="light">
                  {getBoardCurrencySymbol(board)}
                  {event.registrationPrice.toFixed(2)}
                </Badge>
              )}
            </Group>
          </Button>
        </>
      )}
      {selfRegistration && (
        <>
          {checkingIn && (
            <Text size="sm">
              Checking you in <Loader size="xs" />
            </Text>
          )}
          {selfRegistration.approved && !checkingIn && (
            <Group wrap="nowrap">
              <ThemeIcon variant="light" color="green" size="xs">
                <IconCheck />
              </ThemeIcon>
              <Text size="sm">You are registered for this event</Text>
            </Group>
          )}
          {!selfRegistration.approved && (
            <Group wrap="nowrap">
              <ThemeIcon variant="light" size="xs">
                <IconClock />
              </ThemeIcon>
              <Text size="sm">You are awaiting approval for this event</Text>
            </Group>
          )}
          {!selfRegistration.paid && event.requireRegistrationPayment && (
            <Stack>
              <Group wrap="nowrap">
                <ThemeIcon variant="light" size="xs">
                  {getBoardCurrencySymbol(board)}
                </ThemeIcon>
                {event.registrationPrice && (
                  <Text size="sm">
                    Your payment of {getBoardCurrencySymbol(board)}
                    {event.registrationPrice.toFixed(2)} is required for this
                    event
                  </Text>
                )}
                {!event.registrationPrice && (
                  <Text size="sm">Your payment is required for this event</Text>
                )}
              </Group>
              {!!(event.registrationPrice > 0) && (
                <Button
                  variant="filled"
                  onClick={() => openStripePayment(event)}
                  loading={isLoadingStripePayment}
                  disabled={isLoadingStripePayment}
                  leftSection={<IconBrandStripe size={16} />}
                  color="indigo"
                >
                  Pay securely with Stripe
                </Button>
              )}
              {userHasToken && canPayWithToken && (
                <Button
                  variant="filled"
                  onClick={() => payWithToken()}
                  loading={isLoadingStripePayment}
                  disabled={isLoadingStripePayment}
                  leftSection={<IconCoin size={16} />}
                >
                  Pay with an entry token
                  {!!(tokenBalance.quantity > 1) && (
                    <>({tokenBalance.quantity} available)</>
                  )}
                </Button>
              )}
              {userHasToken && canPurchaseTokens && (
                <Button
                  variant="default"
                  onClick={() => showTokenPackOptions()}
                  leftSection={<IconCoins size={16} />}
                >
                  Purchase additional tokens
                </Button>
              )}
              {!userHasToken && canPurchaseTokens && (
                <Button
                  variant="filled"
                  onClick={() => showTokenPackOptions()}
                  leftSection={<IconCoins size={16} />}
                >
                  Purchase a {tokenType?.name} token pack
                </Button>
              )}
            </Stack>
          )}
          {selfRegistration.paid && event.requireRegistrationPayment && (
            <Group wrap="nowrap">
              <ThemeIcon variant="light" color="green" size="xs">
                <IconCheck />
              </ThemeIcon>
              <Text size="sm">
                Your payment has been received for this event
              </Text>
            </Group>
          )}
          {selfRegistration.approved && selfRegistration.checkedIn && (
            <Group wrap="nowrap">
              <ThemeIcon variant="light" color="green" size="xs">
                <IconCheck />
              </ThemeIcon>
              <Text size="sm">
                You are checked in for this event. Waiting to start.
              </Text>
            </Group>
          )}
          {selfRegistration.approved && !selfRegistration.checkedIn && (
            <Group wrap="nowrap">
              <ThemeIcon variant="light" size="xs">
                <IconClock />
              </ThemeIcon>
              <Text size="sm">Check in is required for this event</Text>
            </Group>
          )}
          <Button
            variant="default"
            leftSection={<IconTrash size={16} />}
            onClick={onClickUnregister}
          >
            Cancel my registration
          </Button>
        </>
      )}
    </Stack>
  );
};
export default SelfRegistration;
